import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { api } from '@/lib/api';
import { getStudyTutorPrice } from './studyApi';
import { StudyMindMap } from './StudyMindMap';
type Proposal = {
  request_id: string;
  document_id: string;
  snapshot: string;
  expected_revision: number;
  map: {
    nodes: Array<{ id: string; label: string; notes: string }>;
    edges: Array<{ from: string; to: string }>;
  };
  context_truncated: boolean;
};
export function StudyMapCoach({
  documentId,
  label,
  enabled,
  onSaved,
}: {
  documentId: string;
  label: string;
  enabled: boolean;
  onSaved: () => Promise<unknown>;
}) {
  const key = useRef<string | null>(null);
  const price = useQuery({
    queryKey: ['study-tutor-price'],
    queryFn: getStudyTutorPrice,
    retry: false,
  });
  const propose = useMutation({
    mutationFn: () => {
      if (!price.data) throw new Error('Tutor price is not ready.');
      key.current ??= crypto.randomUUID();
      return api<Proposal>('/study/knowledge/artifacts/concept-maps/proposal', {
        method: 'POST',
        body: {
          document_id: documentId,
          request_id: key.current,
          expected_stars: price.data.stars,
        },
      });
    },
  });
  const apply = useMutation({
    mutationFn: () => {
      const p = propose.data;
      if (!p) throw new Error('Preview first.');
      return api('/study/knowledge/artifacts/concept-maps/apply-proposal', {
        method: 'POST',
        body: {
          document_id: p.document_id,
          request_id: p.request_id,
          expected_snapshot: p.snapshot,
          expected_revision: p.expected_revision,
        },
      });
    },
    onSuccess: async () => {
      await onSaved();
      propose.reset();
      key.current = null;
    },
  });
  return (
    <div className="mt-4 rounded-2xl border border-hairline bg-canvas p-4">
      <button
        type="button"
        className="btn-pill-secondary"
        disabled={!enabled || !price.data || propose.isPending}
        onClick={() => propose.mutate()}
      >
        {propose.isPending
          ? 'Preparing map…'
          : `AI map ${label}${price.data ? ` · ${price.data.stars}★` : ''}`}
      </button>
      <p className="mt-2 text-xs text-slate2">
        AI suggests relationships between 2–20 of your saved concepts. Review them before saving.
        A connected map does not show whether you can explain each idea yet.
      </p>
      {propose.isError || apply.isError ? (
        <p role="alert" className="mt-3 text-sm text-brand-coral">
          {(apply.error ?? propose.error)?.message} Your saved map stays as it is.
        </p>
      ) : null}
      {propose.data && (
        <>
          <p className="mt-4 text-sm font-bold">
            AI suggestion · not saved
            {propose.data.context_truncated ? ' · Shortened notes used' : ''}
          </p>
          <StudyMindMap map={propose.data.map} />
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              className="btn-pill-primary"
              disabled={apply.isPending}
              onClick={() => apply.mutate()}
            >
              {apply.isPending ? 'Saving…' : 'Save reviewed map'}
            </button>
            <button
              type="button"
              className="btn-pill-ghost"
              disabled={apply.isPending}
              onClick={() => {
                propose.reset();
                key.current = null;
              }}
            >
              Discard suggestion
            </button>
          </div>
        </>
      )}
    </div>
  );
}
